import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { api } from '@/api';
import { PaginatedOcsConsoleListFilters, OcsConsoleByIdParams } from '@/api/apis/resources';
import { queryClient } from '@/queries';
import { hubQueries } from '@/queries/hub';
import { DEFAULT_INFINITE_QUERY_OPTIONS } from '../utils';
import { ocsConsolesQueryKeys } from '@/queries/ocs-consoles/ocs-consoles.keys';

const allOcsConsolesPaginationParams: PaginatedOcsConsoleListFilters = {
  itemsPerPage: 100,
  pageIndex: 0,
};

const getOcsConsoleByIdWithHub = async (consoleId: string, params?: OcsConsoleByIdParams) => {
  const data = await api.resource.getOcsConsoleById(consoleId, params);

  if (data) {
    const hubs = queryClient.getQueryData(hubQueries.all().queryKey);

    if (hubs) {
      return {
        ...data,
        hub: hubs.items.find((h) => h.id === data.nopHub),
      };
    }
  }

  return data;
};

const getOcsConsoleListWithHubs = async (pagination: PaginatedOcsConsoleListFilters) => {
  const data = await api.resource.getOcsConsolesList(pagination);

  if (data) {
    const hubs = queryClient.getQueryData(hubQueries.all().queryKey);

    if (hubs) {
      return {
        ...data,
        items: data.items.map((c) => ({
          ...c,
          hub: hubs.items.find((h) => h.id === c.nopHub),
        })),
      };
    }
  }

  return data;
};

export const ocsConsoleQueries = {
  all: () =>
    queryOptions({
      queryKey: ocsConsolesQueryKeys.getList(allOcsConsolesPaginationParams),
      queryFn: () => api.resource.getOcsConsolesList(allOcsConsolesPaginationParams),
    }),
  list: (filters: PaginatedOcsConsoleListFilters) =>
    queryOptions({
      queryKey: ocsConsolesQueryKeys.getList(filters),
      queryFn: () => getOcsConsoleListWithHubs(filters),
      placeholderData: keepPreviousData,
    }),
  item: (consoleId: string, params?: OcsConsoleByIdParams) =>
    queryOptions({
      queryKey: ocsConsolesQueryKeys.getItem(consoleId, params),
      queryFn: () => getOcsConsoleByIdWithHub(consoleId, params),
    }),
  infiniteList: (filters: PaginatedOcsConsoleListFilters, enabled: boolean = true) =>
    infiniteQueryOptions({
      queryKey: ocsConsolesQueryKeys.getInfiniteList(filters),
      queryFn: ({ pageParam: pageIndex }) => api.resource.getOcsConsolesList({ ...filters, pageIndex }),
      enabled,
      ...DEFAULT_INFINITE_QUERY_OPTIONS,
    }),
};

const useOcsConsolesListQuery = (filters: PaginatedOcsConsoleListFilters) => {
  return useQuery(ocsConsoleQueries.list(filters));
};

const useOcsConsoleByIdQuery = (consoleId: string, params?: OcsConsoleByIdParams) => {
  return useQuery({
    ...ocsConsoleQueries.item(consoleId, params),
    staleTime: 60 * 1000,
    initialData: () => {
      const query = queryClient.getQueryData(ocsConsoleQueries.all().queryKey);
      return query?.items.find((c: { id: string }) => c.id === consoleId);
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(ocsConsoleQueries.all().queryKey)?.dataUpdatedAt,
    placeholderData: keepPreviousData,
    retry: false,
  });
};

const useInfiniteOcsConsoleListQuery = (filters: PaginatedOcsConsoleListFilters, enabled?: boolean) => {
  return useInfiniteQuery(ocsConsoleQueries.infiniteList({ itemsPerPage: 20, pageIndex: 0, ...filters }, enabled));
};

export { useOcsConsolesListQuery, useOcsConsoleByIdQuery, useInfiniteOcsConsoleListQuery };
